import {
  Location,
  PaginatedVehiclesResponse,
  Status,
  Vehicle,
  VehicleAuction,
  VehicleDetail,
  VehicleInformation,
  VehiclePartial,
  VehiclePricing,
  VehicleVendorInformation,
  defaultVehicle,
} from 'models'
import { z } from 'zod'
import { parseWithSchema } from './zodValidation'

const unknownArraySchema = z.array(z.unknown()).default([]).catch([])
const nullableArrayAsArraySchema = z
  .array(z.unknown())
  .nullable()
  .default([])
  .catch([])
  .transform((value) => value ?? [])
const nonNegativeCoercedIntSchema = z.coerce.number().int().min(0)
const positiveCoercedIntSchema = z.coerce.number().int().min(1)

const pickDefaultValues = <T extends Record<string, unknown>, K extends keyof T>(
  source: T,
  keys: readonly K[],
) =>
  keys.reduce<Record<K, T[K]>>(
    (acc, key) => {
      acc[key] = source[key]
      return acc
    },
    {} as Record<K, T[K]>,
  )

const buildSchemaShape = <K extends string, T, S extends z.ZodTypeAny>(
  defaults: Record<K, T>,
  keys: readonly K[],
  buildFieldSchema: (fallback: T) => S,
) =>
  keys.reduce<Record<K, S>>(
    (acc, key) => {
      acc[key] = buildFieldSchema(defaults[key])
      return acc
    },
    {} as Record<K, S>,
  )

const buildStringFieldSchema = (fallback: string | null) => {
  if (fallback === null) {
    return z.string().nullable().default(null).catch(null)
  }

  return z.string().default(fallback).catch(fallback)
}

const buildProvidedFreFieldSchema = (fallback: string | null) =>
  z
    .union([z.string(), z.number(), z.null()])
    .transform((value) => {
      if (value === null) return fallback
      if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : fallback
      }
      return value
    })
    .default(fallback)
    .catch(fallback)

const buildNumberFieldSchema = (fallback: number | null) => {
  if (fallback === null) {
    return z.number().nullable().default(null).catch(null)
  }

  return z.coerce.number().default(fallback).catch(fallback)
}

const buildStatusFieldSchema = (fallback: string | number | null) =>
  z.union([z.string(), z.number(), z.null()]).default(fallback).catch(fallback)

const buildPricingFieldSchema = (fallback: number | null) =>
  z
    .union([z.number(), z.string(), z.null()])
    .transform((value) => {
      if (value === null) return fallback
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value
      }
      if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value)
        if (Number.isFinite(parsed)) {
          return parsed
        }
      }
      return fallback
    })
    .default(fallback)
    .catch(fallback)

const vehicleStatusKeys = [
  'administrativeStatus',
  'logisticStatus',
  'generalStatus',
  'auctionStatus',
  'registrationDocumentStatus',
  'qualityControlStatus',
  'blockingReason',
] as const satisfies ReadonlyArray<keyof Status>

const vehiclePartialStringKeys = [
  'licensePlate',
  'bodyType',
  'type',
  'dateProduction',
  'dateDelivery',
  'vin',
  'dateFirstReg',
  'color',
  'typeRegDoc',
  'fuelType',
  'bodyTypeRegDoc',
] as const satisfies ReadonlyArray<keyof VehiclePartial>

const vehicleDetailStringKeys = [
  'normalVersion',
  'make',
  'typeSpare',
  'version',
] as const satisfies ReadonlyArray<keyof VehicleDetail>

const vehicleDetailNumberKeys = ['numberOfSeats', 'taxHp'] as const satisfies ReadonlyArray<
  keyof VehicleDetail
>

const vehicleInformationStringKeys = [
  'dateCreationDossier',
  'registrationCertificateReceivedDate',
  'receiptDate',
  'observationsVendeur',
] as const satisfies ReadonlyArray<keyof VehicleInformation>

const vehicleInformationNumberKeys = [
  'mileage',
  'stockInElapsedDays',
  'noteExt',
  'noteInt',
  'noteCT',
  'noteBilanExpert',
] as const satisfies ReadonlyArray<keyof VehicleInformation>

const vehiclePricingKeys = [
  'estimationApporteur',
  'coteArgusKm',
  'coteArgusPriceDate',
  'coteMarche',
  'reserveTTCHF',
] as const satisfies ReadonlyArray<keyof VehiclePricing>

const vehicleVendorInformationKeys = ['providedFRE'] as const satisfies ReadonlyArray<
  keyof VehicleVendorInformation
>

const locationKeys = ['locationName'] as const satisfies ReadonlyArray<keyof Location>

const auctionKeys = ['auctionStartDateTime'] as const satisfies ReadonlyArray<keyof VehicleAuction>

const defaultVehicleStatus = pickDefaultValues(defaultVehicle.status, vehicleStatusKeys)
const defaultVehiclePartial = {
  ...pickDefaultValues(defaultVehicle.vehiclePartial, vehiclePartialStringKeys),
  equipments: [...defaultVehicle.vehiclePartial.equipments] as Array<unknown>,
}
const defaultVehicleDetail = {
  ...pickDefaultValues(defaultVehicle.vehicleDetail, vehicleDetailStringKeys),
  ...pickDefaultValues(defaultVehicle.vehicleDetail, vehicleDetailNumberKeys),
}
const defaultVehicleInformation = {
  ...pickDefaultValues(defaultVehicle.information, vehicleInformationStringKeys),
  ...pickDefaultValues(defaultVehicle.information, vehicleInformationNumberKeys),
}
const defaultVehiclePricing = pickDefaultValues(defaultVehicle.pricing, vehiclePricingKeys)
const defaultVehicleVendorInformation = {
  providedFRE: defaultVehicle.vendorInformation?.providedFRE ?? null,
  referenceVendeur: defaultVehicle.vendorInformation?.referenceVendeur ?? null,
}
const defaultLocation = {
  locationName: null,
}
const defaultAuction = {
  auctionStartDateTime: defaultVehicle.auction?.auctionStartDateTime ?? null,
}
const defaultPaginationParams = {
  page: 0,
  limit: 1,
  offset: 0,
}

const vehicleStatusSchema = z
  .object(buildSchemaShape(defaultVehicleStatus, vehicleStatusKeys, buildStatusFieldSchema))
  .passthrough()
  .default(defaultVehicleStatus)
  .catch(defaultVehicleStatus)

const vehiclePartialSchema = z
  .object({
    ...buildSchemaShape(defaultVehiclePartial, vehiclePartialStringKeys, buildStringFieldSchema),
    equipments: unknownArraySchema,
  })
  .passthrough()
  .default(defaultVehiclePartial)
  .catch(defaultVehiclePartial)

const vehicleDetailSchema = z
  .object({
    ...buildSchemaShape(defaultVehicleDetail, vehicleDetailStringKeys, buildStringFieldSchema),
    ...buildSchemaShape(defaultVehicleDetail, vehicleDetailNumberKeys, buildNumberFieldSchema),
  })
  .passthrough()
  .default(defaultVehicleDetail)
  .catch(defaultVehicleDetail)

const vehicleInformationSchema = z
  .object({
    ...buildSchemaShape(
      defaultVehicleInformation,
      vehicleInformationNumberKeys,
      buildNumberFieldSchema,
    ),
    ...buildSchemaShape(
      defaultVehicleInformation,
      vehicleInformationStringKeys,
      buildStringFieldSchema,
    ),
  })
  .passthrough()
  .default(defaultVehicleInformation)
  .catch(defaultVehicleInformation)

const vehiclePricingSchema = z
  .object(buildSchemaShape(defaultVehiclePricing, vehiclePricingKeys, buildPricingFieldSchema))
  .passthrough()
  .default(defaultVehiclePricing)
  .catch(defaultVehiclePricing)

const vehicleVendorInformationSchema = z
  .object({
    ...buildSchemaShape(
      defaultVehicleVendorInformation,
      vehicleVendorInformationKeys,
      buildProvidedFreFieldSchema,
    ),
    referenceVendeur: z.string().nullable().default(null).catch(null),
  })
  .passthrough()
  .default(defaultVehicleVendorInformation)
  .catch(defaultVehicleVendorInformation)

const locationSchema = z
  .object(buildSchemaShape(defaultLocation, locationKeys, buildStringFieldSchema))
  .passthrough()
  .default(defaultLocation)
  .catch(defaultLocation)

const auctionSchema = z
  .object(buildSchemaShape(defaultAuction, auctionKeys, buildStringFieldSchema))
  .passthrough()
  .default(defaultAuction)
  .catch(defaultAuction)

const vehicleItemSchema = z
  .object({
    no: buildStringFieldSchema(defaultVehicle.no),
    entityId: buildStringFieldSchema(defaultVehicle.entityId),
    contactId: buildStringFieldSchema(defaultVehicle.contactId),
    status: vehicleStatusSchema,
    vehiclePartial: vehiclePartialSchema,
    vehicleDetail: vehicleDetailSchema,
    information: vehicleInformationSchema,
    pricing: vehiclePricingSchema,
    vendorInformation: vehicleVendorInformationSchema.nullable().default(null).catch(null),
    location: locationSchema.nullable().default(null).catch(null),
    pickupLocation: locationSchema.nullable().default(null).catch(null),
    auction: auctionSchema.nullable().default(null).catch(null),
    documents: nullableArrayAsArraySchema,
    photos: nullableArrayAsArraySchema,
    prestations: unknownArraySchema,
  })
  .passthrough()

const vehiclesSearchResponseSchema = z
  .object({
    data: z.array(z.unknown()),
    totalCount: nonNegativeCoercedIntSchema.optional().default(0).catch(0),
    paginationParams: z
      .object({
        page: nonNegativeCoercedIntSchema,
        limit: positiveCoercedIntSchema,
        offset: nonNegativeCoercedIntSchema,
      })
      .passthrough()
      .optional()
      .default(defaultPaginationParams)
      .catch(defaultPaginationParams),
  })
  .passthrough()

const createFallbackVehicleItem = () =>
  ({
    ...defaultVehicle,
    no: defaultVehicle.no,
    entityId: defaultVehicle.entityId,
    contactId: defaultVehicle.contactId,
    status: { ...defaultVehicle.status, ...defaultVehicleStatus },
    vehiclePartial: { ...defaultVehicle.vehiclePartial, ...defaultVehiclePartial },
    vehicleDetail: { ...defaultVehicle.vehicleDetail, ...defaultVehicleDetail },
    information: { ...defaultVehicle.information, ...defaultVehicleInformation },
    pricing: { ...defaultVehicle.pricing, ...defaultVehiclePricing },
    vendorInformation: {
      ...(defaultVehicle.vendorInformation || {}),
      ...defaultVehicleVendorInformation,
    },
    location: null,
    pickupLocation: null,
    auction: null,
    documents: [],
    photos: [],
    prestations: [],
  }) as unknown as Vehicle

const parseVehicleItem = (item: unknown): Vehicle => {
  const result = vehicleItemSchema.safeParse(item)
  if (!result.success) {
    return createFallbackVehicleItem()
  }

  return result.data as unknown as Vehicle
}

const parseVehiclesSearchResponse = (payload: unknown): PaginatedVehiclesResponse => {
  const parsedEnvelope = parseWithSchema(vehiclesSearchResponseSchema, payload, 'vehicles/search')
  const items = parsedEnvelope.data.map((item) => parseVehicleItem(item))
  const parsedTotalCount =
    typeof parsedEnvelope.totalCount === 'number' && Number.isFinite(parsedEnvelope.totalCount)
      ? parsedEnvelope.totalCount
      : 0
  const totalCount = Math.max(parsedTotalCount, items.length)

  return {
    items,
    totalCount,
  }
}

export { parseVehiclesSearchResponse }
